<script setup>
import { ref, computed } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import TieredPricingCards from '@/Components/Forms/TieredPricingCards.vue'

const props = defineProps({
  user: Object,
  teachingMethods: Array,
  offerings: Array,
  pricingLimits: Object,
})

const loading = ref(false)
const successMessage = ref('')
const errors = ref({})

// Teaching methods selection - Initialize with user's methods
const selectedMethods = ref(props.user?.teachingMethods?.map(m => m.id) || [])

// The tutor's own price, the recommended one when they have none yet
const goldTier = (props.user?.pricingTiers || props.user?.pricing_tiers || []).find(tier => tier.tier.toLowerCase() === 'gold')
const goldPlanPrice = ref(goldTier ? parseFloat(goldTier.price) : props.pricingLimits.gold.average)

/**
 * Whether this tutor offers the one product the site sells.
 *
 * This page used to list the four teaching methods behind them — a tutor read
 * "Group Tutoring" here and "Bindle Live" on the card immediately below it —
 * and worked out which plans to show by matching those names, so renaming a row
 * in the database changed a tutor's pricing. Each offering declares its tier and
 * the capability that identifies it.
 */
const offeredOfferings = computed(() =>
  (props.offerings || []).filter(offering => selectedMethods.value.includes(offering.primary_method_id))
)

const offersTier = (tier) => offeredOfferings.value.some(offering => offering.tier === tier)

const showGoldPlan = computed(() => offersTier('gold'))

const savePricing = async () => {
  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const response = await fetch('/teacher/settings/pricing', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        teaching_method_ids: selectedMethods.value,
        gold_plan_price: goldPlanPrice.value,
        offer_gold_plan: showGoldPlan.value,
      })
    })

    const data = await response.json()

    if (!response.ok) {
      errors.value = { general: data.message || 'Failed to save. Please try again.' }
      loading.value = false
      return
    }

    successMessage.value = 'Pricing updated successfully!'
    loading.value = false

    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <!-- What the tutor offers, named the way the rest of the site names it -->
      <div class="bg-white rounded-2xl p-8 border-2 border-gray-200">
        <div class="mb-6">
          <h2 class="text-2xl font-bold text-gray-900 mb-2 flex items-center">
            <span class="material-symbols-outlined text-brand-blue mr-3">school</span>
            What You Offer
          </h2>
          <p class="text-gray-600">The products you teach on Bindle. The ones you offer are highlighted.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="offering in offerings"
            :key="offering.key"
            :class="[
              'relative inline-flex items-center gap-3 px-4 py-4 border-2 rounded-xl transition-all overflow-hidden',
              selectedMethods.includes(offering.primary_method_id)
                ? 'bg-white border-gray-300 shadow-sm'
                : 'bg-gray-50 border-gray-200 opacity-60'
            ]"
          >
            <!-- Top-right corner indicator for what is offered -->
            <div
              v-if="selectedMethods.includes(offering.primary_method_id)"
              class="absolute top-0 right-0 w-0 h-0"
              :style="{
                borderStyle: 'solid',
                borderWidth: '0 40px 40px 0',
                borderColor: `transparent ${offering.color} transparent transparent`
              }"
            ></div>

            <span
              v-if="selectedMethods.includes(offering.primary_method_id)"
              class="material-symbols-outlined absolute top-1 right-1 text-white text-sm z-10"
              style="font-size: 14px;"
            >
              check
            </span>

            <div class="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-brand-soft">
              <span class="material-symbols-outlined text-2xl text-brand">{{ offering.icon_name }}</span>
            </div>
            <div class="flex-1">
              <span class="text-base font-semibold text-gray-900 block">
                {{ offering.name }}
                <span
                  v-if="offering.coming_soon"
                  class="ml-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                >
                  Coming soon
                </span>
              </span>
              <span class="text-xs text-gray-500">{{ offering.description }}</span>
            </div>
          </div>
        </div>
      </div>

      <TieredPricingCards
        :pricing-limits="pricingLimits"
        :initial-pricing="{ gold: goldPlanPrice }"
        :has-gold-methods="showGoldPlan"
        @update:pricing="pricing => goldPlanPrice = pricing.gold"
      />

      <div class="rounded-2xl border border-line-brand bg-brand-soft p-5 flex items-start gap-3">
        <span class="material-symbols-outlined text-brand">info</span>
        <p class="text-sm text-brand-soft-text">
          A new price applies to students who book you from now on. The students you already teach, and any request waiting for your answer, keep the price they agreed to.
        </p>
      </div>

      <div class="flex justify-end">
        <button
          @click="savePricing"
          :disabled="loading"
          class="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <span v-if="loading" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
          <span>{{ loading ? 'Saving...' : 'Save Pricing' }}</span>
        </button>
      </div>
    </div>
  </SettingsLayout>
</template>
